import React, { useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  User, List, Calendar, Star, DollarSign, Calculator,
  Search, Award, Check, ChevronRight, TrendingUp
} from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";

const ComoFuncionaPage: React.FC = () => {
  const [tabValue, setTabValue] = useState("mentores");
  
  return (
    <div className="bg-[#0f141c] min-h-screen">
      <Navbar />
      
      {/* Hero Section - Otimizado */}
      <section className="relative pt-32 pb-16 px-4">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0f141c] via-[#112236] to-[#181D24]"></div>
        
        <div className="container relative mx-auto max-w-5xl text-center">
          <h1 className="text-5xl md:text-6xl font-extrabold mb-8 text-[#00B6F0]">
            COMO FUNCIONA
          </h1>
          <p className="text-xl md:text-2xl text-white/90 mb-12 max-w-3xl mx-auto leading-relaxed">
            Nossa plataforma conecta jogadores a mentores profissionais, 
            oferecendo uma experiência de aprendizado personalizada para 
            alavancar seu desempenho nos games.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Button 
              onClick={() => setTabValue("mentores")}
              className={`px-8 py-6 text-lg font-bold ${tabValue === "mentores" 
                ? "bg-[#00B6F0] text-black hover:bg-[#51EFFF]" 
                : "bg-[#223342] text-white hover:bg-[#334455]"
              } transition-colors duration-200`}
            >
              Para Mentores
            </Button>
            <Button 
              onClick={() => setTabValue("jogadores")} 
              className={`px-8 py-6 text-lg font-bold ${tabValue === "jogadores" 
                ? "bg-[#00B6F0] text-black hover:bg-[#51EFFF]" 
                : "bg-[#223342] text-white hover:bg-[#334455]"
              } transition-colors duration-200`}
            >
              Para Jogadores
            </Button>
          </div>
        </div>
      </section>
      
      {/* Tabs Section */}
      <section className="container mx-auto px-4 py-12 max-w-6xl">
        <Tabs defaultValue={tabValue} value={tabValue} onValueChange={setTabValue} className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-12 bg-[#181D24]">
            <TabsTrigger value="mentores" className="data-[state=active]:bg-[#223342] data-[state=active]:text-[#00B6F0] py-4 text-lg">
              Para Mentores
            </TabsTrigger>
            <TabsTrigger value="jogadores" className="data-[state=active]:bg-[#223342] data-[state=active]:text-[#00B6F0] py-4 text-lg">
              Para Jogadores
            </TabsTrigger>
          </TabsList>
          
          {/* Mentores Content */}
          <TabsContent value="mentores" className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* Feature Card 1 */}
              <FeatureCard 
                icon={<User className="text-[#00B6F0] w-8 h-8" />} 
                title="Crie seu Perfil"
                content="Registre-se facilmente e construa seu perfil profissional, incluindo suas redes sociais e agendamento de horários disponíveis."
              />
              
              {/* Feature Card 2 */}
              <FeatureCard 
                icon={<List className="text-[#00B6F0] w-8 h-8" />} 
                title="Divulgue suas Mentorias"
                content="Cadastre suas opções de mentoria, definindo jogo, duração, modalidade e preço."
              />
              
              {/* Feature Card 3 */}
              <FeatureCard 
                icon={<Calendar className="text-[#00B6F0] w-8 h-8" />} 
                title="Gerencie sua Agenda"
                content="Receba notificações em tempo real sobre novas sessões agendadas e insira links de mentoria (Zoom, Discord) após a confirmação de pagamento."
              />
              
              {/* Feature Card 4 */}
              <FeatureCard 
                icon={<Star className="text-[#00B6F0] w-8 h-8" />} 
                title="Avaliações e Reputação"
                content="Receba avaliações dos jogadores, aumentando sua reputação na plataforma."
              />
              
              {/* Feature Card 5 */}
              <FeatureCard 
                icon={<DollarSign className="text-[#00B6F0] w-8 h-8" />} 
                title="Ganhos Atraentes"
                content="Com nossa calculadora interativa, simule seus potenciais ganhos e descubra quanto pode lucrar com suas sessões de mentoria."
                className="md:col-span-2 lg:col-span-1"
              />
            </div>

            {/* Simulador de Ganhos */}
            <EarningsSimulator />
          </TabsContent>
          
          {/* Jogadores Content */}
          <TabsContent value="jogadores" className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Feature Card 1 */}
              <FeatureCard 
                icon={<Search className="text-[#00B6F0] w-8 h-8" />} 
                title="Descubra os Melhores Mentores"
                content="Utilize nossos filtros para encontrar o mentor ideal por jogo, preço e disponibilidade."
              />
              
              {/* Feature Card 2 */}
              <FeatureCard 
                icon={<Calendar className="text-[#00B6F0] w-8 h-8" />} 
                title="Agende Sessões Personalizadas"
                content="Marque sessões individuais ou em grupo e pague com segurança através do Stripe."
              />
              
              {/* Feature Card 3 */}
              <FeatureCard 
                icon={<Award className="text-[#00B6F0] w-8 h-8" />} 
                title="Aprimore Suas Habilidades"
                content="Participe de mentorias que combinam conhecimento técnico e experiência prática."
              />
              
              {/* Feature Card 4 */}
              <FeatureCard 
                icon={<Check className="text-[#00B6F0] w-8 h-8" />} 
                title="Avalie e Compartilhe"
                content="Após cada sessão, avalie seu mentor e compartilhe suas experiências com outros jogadores."
              />
            </div>
          </TabsContent>
        </Tabs>
      </section>
      
      {/* Call to Action Section - Otimizado */}
      <section className="py-16 px-4 mb-12">
        <div className="container mx-auto max-w-5xl relative rounded-xl p-12 bg-gradient-to-r from-[#112236] to-[#181D24]">
          <div className="text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-[#00B6F0]">
              Pronto para começar sua jornada?
            </h2>
            <p className="text-lg md:text-xl text-white/80 mb-8 max-w-2xl mx-auto">
              Escolha seu caminho e junte-se à comunidade da Arena Pro Player hoje mesmo.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link to="/cadastro">
                <Button className="px-6 py-6 text-lg bg-gradient-to-r from-[#00B6F0] to-[#0099CC] hover:from-[#00C7FF] hover:to-[#00AADD] text-black font-bold">
                  Quero ser um Mentor <ChevronRight className="ml-2" />
                </Button>
              </Link>
              <Link to="/mentores">
                <Button className="px-6 py-6 text-lg bg-[#223342] text-white hover:bg-[#334455] font-bold border border-[#00B6F0]/50">
                  Quero encontrar um Mentor <ChevronRight className="ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Feature Card Component - Otimizado
const FeatureCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  content: string;
  className?: string;
}> = ({ icon, title, content, className = "" }) => {
  return (
    <Card className={`transition-transform duration-200 hover:scale-105 border-[#223342] bg-[#181D24] shadow-lg ${className}`}>
      <CardContent className="p-6">
        <div className="flex items-start mb-4">
          <div className="p-3 rounded-lg bg-[#223342] mr-4">
            {icon}
          </div>
          <h3 className="text-xl font-bold text-white">{title}</h3>
        </div>
        <p className="text-white/70 text-base leading-relaxed">{content}</p>
      </CardContent>
    </Card>
  );
};

// Simulador de Ganhos Component
const EarningsSimulator: React.FC = () => {
  const [sessionPrice, setSessionPrice] = useState(50);
  const [sessionsPerWeek, setSessionsPerWeek] = useState(10);
  
  const platformFee = 0.20; // 20% de taxa
  const earningsPerSession = sessionPrice * (1 - platformFee);
  const weeklyEarnings = earningsPerSession * sessionsPerWeek;
  const monthlyEarnings = weeklyEarnings * 4.3; // média de semanas por mês
  
  return (
    <Card className="border-[#223342] bg-[#181D24] shadow-xl">
      <CardHeader>
        <CardTitle className="flex items-center text-2xl text-[#00B6F0]">
          <Calculator className="mr-3 h-6 w-6" />
          Simulador de Ganhos para Mentores
        </CardTitle>
        <p className="text-white/70">
          Calcule seus ganhos potenciais como mentor na nossa plataforma
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label htmlFor="session-price" className="text-white">
              Preço por Sessão (R$)
            </Label>
            <Input
              id="session-price"
              type="number"
              value={sessionPrice}
              onChange={(e) => setSessionPrice(Number(e.target.value))}
              className="bg-[#223342] border-[#334455] text-white"
              min="1"
              max="1000"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="sessions-week" className="text-white">
              Sessões por Semana
            </Label>
            <Input
              id="sessions-week"
              type="number"
              value={sessionsPerWeek}
              onChange={(e) => setSessionsPerWeek(Number(e.target.value))}
              className="bg-[#223342] border-[#334455] text-white"
              min="1"
              max="50"
            />
          </div>
        </div>
        
        <div className="bg-[#112236] rounded-lg p-6 space-y-4">
          <h4 className="text-lg font-semibold text-[#00B6F0] flex items-center">
            <TrendingUp className="mr-2 h-5 w-5" />
            Seus Ganhos Projetados
          </h4>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-sm text-white/60">Por Sessão</div>
              <div className="text-2xl font-bold text-white">
                R$ {earningsPerSession.toFixed(2)}
              </div>
              <div className="text-xs text-white/50">
                (R$ {sessionPrice} - 20% taxa)
              </div>
            </div>
            
            <div className="text-center">
              <div className="text-sm text-white/60">Por Semana</div>
              <div className="text-2xl font-bold text-[#00B6F0]">
                R$ {weeklyEarnings.toFixed(2)}
              </div>
              <div className="text-xs text-white/50">
                {sessionsPerWeek} sessões
              </div>
            </div>
            
            <div className="text-center">
              <div className="text-sm text-white/60">Por Mês</div>
              <div className="text-2xl font-bold text-green-400">
                R$ {monthlyEarnings.toFixed(2)}
              </div>
              <div className="text-xs text-white/50">
                {(sessionsPerWeek * 4.3).toFixed(0)} sessões
              </div>
            </div>
          </div>
          
          <div className="mt-4 p-4 bg-[#00B6F0]/10 rounded-lg border border-[#00B6F0]/20">
            <p className="text-sm text-white/80">
              💡 <strong>Dica:</strong> Nossa taxa de 20% inclui processamento de pagamentos, 
              suporte técnico e marketing da plataforma para te ajudar a encontrar mais alunos.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ComoFuncionaPage;
